/**
 * Soft venue + at-own-risk banner.
 *
 * Shown to logged-in users when our backend reports their acknowledged
 * version is behind the current `VENUE_DISCLAIMER_VERSION`. Dismissable
 * but not blocking — the per-paid-action click-wraps at every paid
 * endpoint are the legal record, and they automatically attach the
 * current version. This banner exists to be the visible front-door
 * notice so a user can never plausibly claim "I never knew Armstrong
 * was a venue".
 *
 * Behaviour:
 *   - Fetches /api/me/venue-disclaimer on mount.
 *   - If `needs_acknowledgement: true`, renders an amber banner with
 *     the key bullet points + an "I acknowledge" button.
 *   - On click → POST /api/me/venue-disclaimer/acknowledge.
 *   - If the user dismisses without clicking, we drop a localStorage
 *     flag that hides the banner for the rest of the session (~ they
 *     get a fresh nudge next session).
 */
import React, { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";

const SESSION_DISMISS_KEY = "venue_ack_banner_dismissed_session";

export default function VenueAckBanner() {
  const { user } = useAuth() || {};
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (sessionStorage.getItem(SESSION_DISMISS_KEY)) {
      setDismissed(true);
      return;
    }
    api
      .get("/me/venue-disclaimer")
      .then((r) => setState(r.data))
      .catch(() => {});
  }, [user]);

  if (!user || !state || !state.needs_acknowledgement || dismissed) return null;

  const acknowledge = async () => {
    setBusy(true);
    try {
      await api.post("/me/venue-disclaimer/acknowledge");
      setState({ ...state, needs_acknowledgement: false });
    } catch {
      // best-effort: stay visible if it errored, user can dismiss
    } finally {
      setBusy(false);
    }
  };

  const dismiss = () => {
    sessionStorage.setItem(SESSION_DISMISS_KEY, "1");
    setDismissed(true);
  };

  return (
    <div
      data-testid="venue-ack-banner"
      className="bg-[#FBF7EC] border-b border-[#E8D9A6] text-[#3B0E07]"
      role="region"
      aria-label="Venue role and at-own-risk acknowledgement"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 py-2.5 flex flex-wrap items-start gap-3 text-[12.5px] leading-snug">
        <div className="flex-1 min-w-[260px]">
          <strong>Armstrong K9 Registry is a venue.</strong> We introduce independent
          breeders, buyers, service providers, transporters, and event
          hosts; we are not a party to any transaction and you use the
          platform at your own risk. We&rsquo;ve updated this notice
          (v{state.current_version}) &mdash; please confirm you understand.{" "}
          <a
            href="/terms"
            className="underline font-semibold"
            data-testid="venue-ack-banner-terms-link"
          >
            Read the terms
          </a>
          .
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={acknowledge}
            disabled={busy}
            className="rounded-full bg-[#0E0E0E] text-white text-[12px] font-semibold px-4 h-8 disabled:opacity-60"
            data-testid="venue-ack-banner-acknowledge"
          >
            {busy ? "Saving…" : "I acknowledge"}
          </button>
          <button
            type="button"
            onClick={dismiss}
            className="rounded-full text-[12px] font-semibold px-3 h-8 text-[#7A1F12] hover:bg-[#F4E5B0]"
            data-testid="venue-ack-banner-dismiss"
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
}
